import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Crosshair, ArrowRight } from "@phosphor-icons/react";
import { fmtYmdTime, fmtYmdSeconds } from "@/lib/dateFormat";

export default function GpsTracking() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tracking, setTracking] = useState(false);
    const [vehicleId, setVehicleId] = useState("");
    const [vehicles, setVehicles] = useState([]);
    const [selected, setSelected] = useState(null);
    const [history, setHistory] = useState([]);

    const load = async () => {
        const [latest, v] = await Promise.all([
            api.get("/gps/latest"),
            api.get("/vehicles", { params: { status: "active" } }),
        ]);
        setList(latest.data);
        setVehicles(v.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const loadHistory = async (vid) => {
        setSelected(vid);
        const { data } = await api.get(`/gps/vehicle/${vid}`);
        setHistory(data);
    };

    const recordOnce = async () => {
        if (!vehicleId) {
            toast.error("車両を選択してください");
            return;
        }
        if (!navigator.geolocation) {
            toast.error("このデバイスは位置情報非対応です");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (p) => {
                try {
                    await api.post("/gps/track", {
                        vehicle_id: vehicleId,
                        lat: p.coords.latitude,
                        lng: p.coords.longitude,
                        speed_kmh: p.coords.speed ? p.coords.speed * 3.6 : null,
                        heading: p.coords.heading,
                        accuracy_m: p.coords.accuracy,
                    });
                    toast.success("現在位置を記録しました");
                    await load();
                } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail));
                }
            },
            (err) => toast.error("位置取得失敗: " + err.message),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const toggleAutoTrack = () => {
        if (tracking) {
            if (window._gpsWatcher) {
                navigator.geolocation.clearWatch(window._gpsWatcher);
                window._gpsWatcher = null;
            }
            setTracking(false);
            toast("自動収集を停止しました");
            return;
        }
        if (!vehicleId) { toast.error("車両を選択してください"); return; }
        if (!navigator.geolocation) { toast.error("このデバイスは位置情報非対応"); return; }
        const id = navigator.geolocation.watchPosition(
            async (p) => {
                try {
                    await api.post("/gps/track", {
                        vehicle_id: vehicleId,
                        lat: p.coords.latitude,
                        lng: p.coords.longitude,
                        speed_kmh: p.coords.speed ? p.coords.speed * 3.6 : null,
                    });
                } catch {}
            },
            () => {},
            { enableHighAccuracy: true, maximumAge: 30000, timeout: 30000 }
        );
        window._gpsWatcher = id;
        setTracking(true);
        toast.success("バックグラウンド自動収集を開始しました");
    };

    return (
        <div className="space-y-5" data-testid="gps-page">
            <div>
                <p className="prc-label-overline">GPS 自動収集</p>
                <h1 className="text-[1.685rem] font-bold tracking-tight">車両位置トラッキング</h1>
                <p className="text-sm text-[var(--prc-muted)] mt-1">点検時に自動で位置記録、または下のスイッチで継続的なバックグラウンド収集が可能です。</p>
            </div>

            <div className="prc-card p-5">
                <p className="prc-label-overline mb-3">記録コントロール</p>
                <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[200px]">
                        <label className="prc-label-overline block mb-1">対象車両</label>
                        <select className="prc-input" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} data-testid="gps-vehicle-select">
                            <option value="">選択</option>
                            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate_number}</option>)}
                        </select>
                    </div>
                    <button onClick={recordOnce} className="prc-btn-ghost inline-flex items-center gap-2" data-testid="gps-record-once-btn">
                        <Crosshair size={16} />現在地を記録
                    </button>
                    <button onClick={toggleAutoTrack} className={`inline-flex items-center gap-2 ${tracking ? "prc-btn-accent" : "prc-btn-primary"}`} data-testid="gps-auto-toggle">
                        <MapPin size={16} />{tracking ? "自動収集を停止" : "自動収集を開始"}
                    </button>
                </div>
                {tracking && <p className="mt-3 text-xs text-[var(--prc-accent)] font-semibold animate-pulse" data-testid="gps-tracking-indicator">● 位置情報を継続的に収集しています</p>}
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="prc-card overflow-x-auto">
                    <div className="p-4 border-b border-[var(--prc-border)] flex justify-between items-center">
                        <p className="prc-label-overline">最終位置（車両別）</p>
                        <span className="text-xs text-[var(--prc-muted)]">{list.length} 件</span>
                    </div>
                    {loading ? <p className="p-4 text-sm">読込中…</p> : list.length === 0 ? (
                        <p className="p-8 text-sm text-[var(--prc-muted)] text-center" data-testid="gps-empty">記録されたGPSデータがありません</p>
                    ) : (
                        <table className="prc-table" data-testid="gps-latest-table">
                            <thead>
                                <tr><th>車両</th><th>緯度・経度</th><th>速度</th><th>記録者</th><th>日時</th></tr>
                            </thead>
                            <tbody>
                                {list.map((g) => (
                                    <tr key={g.vehicle_id} className="cursor-pointer" onClick={() => loadHistory(g.vehicle_id)} data-testid={`gps-latest-${g.vehicle_id}`}>
                                        <td className="font-mono font-semibold">{g.plate_number}</td>
                                        <td className="num text-xs">{g.lat?.toFixed(5)}, {g.lng?.toFixed(5)}</td>
                                        <td className="num">{g.speed_kmh != null ? `${Math.round(g.speed_kmh)} km/h` : "-"}</td>
                                        <td>{g.user_name}</td>
                                        <td className="text-xs">{fmtYmdTime(g.created_at, String(g.created_at).substring(0, 16).replace("T", " "))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="prc-card p-5" data-testid="gps-history-panel">
                    <p className="prc-label-overline mb-3">走行履歴</p>
                    {!selected ? (
                        <p className="text-sm text-[var(--prc-muted)]">左の表から車両をクリックすると履歴を表示します</p>
                    ) : history.length === 0 ? (
                        <p className="text-sm text-[var(--prc-muted)]">履歴データなし</p>
                    ) : (
                        <ul className="text-sm divide-y divide-[var(--prc-border)] max-h-[500px] overflow-auto" data-testid="gps-history-list">
                            {history.map((h) => (
                                <li key={h.id} className="py-2 flex items-center gap-3">
                                    <MapPin size={16} className="text-[var(--prc-muted)] shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-mono text-xs">{h.lat?.toFixed(5)}, {h.lng?.toFixed(5)}</p>
                                        <p className="text-xs text-[var(--prc-muted)]">{fmtYmdSeconds(h.created_at, String(h.created_at).substring(0, 19).replace("T", " "))} · {h.user_name}{h.source ? ` · ${h.source}` : ""}</p>
                                    </div>
                                    {h.speed_kmh != null && <span className="text-xs font-mono">{Math.round(h.speed_kmh)} km/h</span>}
                                    <a
                                        href={`https://www.google.com/maps?q=${h.lat},${h.lng}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs text-[var(--prc-accent)] inline-flex items-center gap-1"
                                        data-testid={`gps-map-link-${h.id}`}
                                    >地図 <ArrowRight size={16} /></a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
